import { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { Field, SelectField, TextArea, PROGRAM_STATUS_OPTIONS } from "./shared";
import { isISODate, fmtMDY, to24, to12, addMinutes, diffMinutes } from "@/lib/dates";
import LiveAgreementPreview from "./LiveAgreementPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_OPTIONS = [["hitting", "Hitting"], ["pitching", "Pitching"], ["strength", "Strength"], ["speed", "Speed & Agility"], ["other", "Other"]];
const AGE_OPTIONS = ["13U & Under", "14U & Up"];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DURATIONS = [45, 60, 75, 90, 120];
const lbl = "text-xs font-bold uppercase tracking-wider text-white/40";
const inp = "mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-[#2563EB] transition-colors [color-scheme:dark]";

// Normalizes a program record for the structured editor (legacy free-text values are surfaced, not guessed).
export function toEditorProgram(p) {
  const legacyDates = [p.start_date, p.end_date].some((d) => d && !isISODate(d));
  const days = p.days?.length ? p.days : WEEKDAYS.filter((d) => (p.day || "").toLowerCase().includes(d.toLowerCase().slice(0, 3)));
  return {
    ...p,
    days,
    start_date: isISODate(p.start_date) ? p.start_date : "",
    end_date: isISODate(p.end_date) ? p.end_date : "",
    legacy_dates: legacyDates ? `${p.start_date || "?"} – ${p.end_date || "?"}` : "",
    time24: to24(p.time),
    end24: to24(p.end_time),
    legacy_time: !to24(p.time) && p.time ? p.time : "",
    payment_terms: p.payment_terms || "paid_in_full",
    renewal_type: "one_time",
    price: p.price === "" || p.price == null ? "" : Number(p.price).toFixed(2),
  };
}

export function fromEditorProgram(p) {
  const { legacy_dates, legacy_time, time24, end24, agreement_missing, agreement_advisories, duplicate_of, ...rest } = p; // eslint-disable-line no-unused-vars
  return { ...rest, time: time24 ? to12(time24) : p.time || "", end_time: end24 ? to12(end24) : "" };
}

function DaysPicker({ value, onChange }) {
  return (
    <div className="sm:col-span-2">
      <span className={lbl}>Training Days *</span>
      <div data-testid="pf-days" className="mt-2 flex flex-wrap gap-2">
        {WEEKDAYS.map((d) => {
          const on = value.includes(d);
          return (
            <button type="button" key={d} data-testid={`pf-day-${d.toLowerCase()}`} aria-pressed={on}
              onClick={() => onChange(on ? value.filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || value.includes(x)))}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${on ? "bg-[#2563EB] text-white" : "border border-white/15 text-white/60 hover:text-white"}`}>
              {d.slice(0, 3)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LocationPicker({ value, onChange, token }) {
  const [locations, setLocations] = useState([]);
  const [adding, setAdding] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const headers = { "X-Admin-Token": token };
  useEffect(() => {
    axios.get(`${API}/admin/locations`, { headers }).then((r) => setLocations(r.data.locations)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const add = async () => {
    const { data } = await axios.post(`${API}/admin/locations`, { name: adding }, { headers });
    setLocations((l) => (l.includes(data.name) ? l : [...l, data.name]));
    onChange(data.name);
    setAdding("");
    setShowAdd(false);
  };
  const opts = value && !locations.includes(value) ? [value, ...locations] : locations;
  return (
    <div className="sm:col-span-2">
      <span className={lbl}>Training Location *</span>
      <div className="flex gap-2">
        <select data-testid="pf-location" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inp}>
          {opts.map((l) => <option key={l} value={l} className="bg-[#0B0F19]">{l}</option>)}
        </select>
        <button type="button" data-testid="pf-location-add-toggle" onClick={() => setShowAdd((s) => !s)} className="mt-2 shrink-0 inline-flex items-center gap-1 rounded-xl border border-white/15 px-3 text-xs font-bold text-white/70 hover:text-white"><Plus size={13} /> New</button>
      </div>
      {showAdd && (
        <div className="mt-2 flex gap-2">
          <input data-testid="pf-location-new" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Facility name, street, city, state ZIP" className={inp} />
          <button type="button" data-testid="pf-location-save" onClick={add} disabled={adding.trim().length < 3} className="mt-2 shrink-0 rounded-xl bg-[#2563EB] px-4 text-xs font-bold text-white disabled:opacity-50">Save</button>
        </div>
      )}
    </div>
  );
}

export default function ProgramFormFields({ p, set, programs, token }) {
  const upd = (patch) => set({ ...p, ...patch });
  const customAge = p.age_group && !AGE_OPTIONS.includes(p.age_group);
  const dateError = p.start_date && p.end_date && p.end_date < p.start_date ? "End date cannot be earlier than the start date." : "";
  const setStart24 = (v) => upd({ time24: v, end24: p.session_minutes ? addMinutes(v, p.session_minutes) : p.end24 });
  const setDuration = (m) => upd({ session_minutes: m, end24: p.time24 && m ? addMinutes(p.time24, m) : p.end24 });
  const setEnd24 = (v) => { const d = diffMinutes(p.time24, v); upd({ end24: v, session_minutes: d > 0 ? d : p.session_minutes }); };
  return (
    <>
      <Field label="Program Name *" testid="pf-title" value={p.title} onChange={(v) => upd({ title: v })} full />
      <SelectField label="Program Category *" testid="pf-type" value={p.program_type} onChange={(v) => upd({ program_type: v })} options={CATEGORY_OPTIONS} />
      <SelectField label="Registration Status" testid="pf-status" value={p.status} onChange={(v) => upd({ status: v })} options={PROGRAM_STATUS_OPTIONS} />
      <div>
        <span className={lbl}>Age Group *</span>
        <select data-testid="pf-age" value={customAge ? "__other" : p.age_group || ""} onChange={(e) => upd({ age_group: e.target.value === "__other" ? " " : e.target.value })} className={inp}>
          <option value="" className="bg-[#0B0F19]">Select…</option>
          {AGE_OPTIONS.map((a) => <option key={a} value={a} className="bg-[#0B0F19]">{a}</option>)}
          <option value="__other" className="bg-[#0B0F19]">Other (custom)…</option>
        </select>
        {customAge && <input data-testid="pf-age-custom" value={p.age_group.trim()} onChange={(e) => upd({ age_group: e.target.value || " " })} placeholder="Custom age group" className={inp} />}
      </div>
      <Field label="Coach" testid="pf-coach" value={p.coach} onChange={(v) => upd({ coach: v })} />
      <label className="block">
        <span className={lbl}>Start Date *</span>
        <input data-testid="pf-start" type="date" value={p.start_date} max={p.end_date || undefined} onChange={(e) => upd({ start_date: e.target.value })} className={inp} />
        {p.start_date && <span className="mt-1 block text-xs font-semibold text-white/45">{fmtMDY(p.start_date)}</span>}
      </label>
      <label className="block">
        <span className={lbl}>End Date *</span>
        <input data-testid="pf-end" type="date" value={p.end_date} min={p.start_date || undefined} onChange={(e) => upd({ end_date: e.target.value })} className={inp} />
        {p.end_date && <span className="mt-1 block text-xs font-semibold text-white/45">{fmtMDY(p.end_date)}</span>}
      </label>
      {dateError && <p data-testid="pf-date-error" className="sm:col-span-2 text-xs font-bold text-red-300">{dateError}</p>}
      {p.legacy_dates && <p data-testid="pf-legacy-dates" className="sm:col-span-2 text-xs font-semibold text-amber-300">Previously entered as text: “{p.legacy_dates}”. Pick the calendar dates above.</p>}
      <DaysPicker value={p.days || []} onChange={(days) => upd({ days })} />
      <label className="block">
        <span className={lbl}>Session Start Time *</span>
        <input data-testid="pf-time" type="time" value={p.time24 || ""} onChange={(e) => setStart24(e.target.value)} className={inp} />
      </label>
      <label className="block">
        <span className={lbl}>Session Duration (minutes) *</span>
        <select data-testid="pf-duration" value={DURATIONS.includes(Number(p.session_minutes)) ? Number(p.session_minutes) : p.session_minutes ? "custom" : ""} onChange={(e) => e.target.value !== "custom" && setDuration(Number(e.target.value))} className={inp}>
          <option value="" className="bg-[#0B0F19]">Select…</option>
          {DURATIONS.map((d) => <option key={d} value={d} className="bg-[#0B0F19]">{d} minutes</option>)}
          {p.session_minutes && !DURATIONS.includes(Number(p.session_minutes)) && <option value="custom" className="bg-[#0B0F19]">{p.session_minutes} minutes</option>}
        </select>
      </label>
      <label className="block">
        <span className={lbl}>Session End Time (auto from duration)</span>
        <input data-testid="pf-endtime" type="time" value={p.end24 || ""} onChange={(e) => setEnd24(e.target.value)} className={inp} />
      </label>
      <label className="block">
        <span className={lbl}>Number of Sessions *</span>
        <input data-testid="pf-sessions" type="number" min={1} step={1} value={p.sessions_count} onChange={(e) => upd({ sessions_count: e.target.value.replace(/[^\d]/g, "") })} className={inp} />
      </label>
      {p.legacy_time && <p className="sm:col-span-2 text-xs font-semibold text-amber-300">Previously entered time: “{p.legacy_time}”. Pick the start time and duration above.</p>}
      <LocationPicker value={p.location} onChange={(v) => upd({ location: v })} token={token} />
      <label className="block">
        <span className={lbl}>Total Program Price (USD) *</span>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-[calc(50%-4px)] text-white/50 font-bold">$</span>
          <input data-testid="pf-price" inputMode="decimal" value={p.price} onChange={(e) => upd({ price: e.target.value.replace(/[^\d.]/g, "") })}
            onBlur={() => p.price !== "" && upd({ price: Number(p.price || 0).toFixed(2) })} className={`${inp} pl-8`} />
        </div>
      </label>
      <Field label="Capacity" type="number" testid="pf-capacity" value={p.capacity} onChange={(v) => upd({ capacity: v })} />
      <SelectField label="Payment Terms" testid="pf-payment-terms" value={p.payment_terms} onChange={(v) => upd({ payment_terms: v })} options={[["paid_in_full", "Paid in full at registration (one-time payment)"]]} />
      <SelectField label="Renewal Type" testid="pf-renewal" value="one_time" onChange={() => {}} options={[["one_time", "One-time enrollment — no automatic renewal"]]} />
      <TextArea label="Description" value={p.description} onChange={(v) => upd({ description: v })} />
      <Field label="Training Focus" testid="pf-focus" value={p.training_focus} onChange={(v) => upd({ training_focus: v })} />
      <Field label="Program-Specific Conditions (blank = None)" testid="pf-conditions" value={p.special_conditions || ""} onChange={(v) => upd({ special_conditions: v })} />
      <SelectField label="Next Cycle (re-enroll email)" testid="pf-next-cycle" value={p.next_program_id || ""} onChange={(v) => upd({ next_program_id: v })}
        options={[["", "Automatic match (same category + age group)"], ["none", "None — don't send re-enroll emails"], ...(programs || []).filter((x) => x.id !== p.id).map((x) => [x.id, `${x.title}${x.age_group ? ` · ${x.age_group}` : ""}${x.start_date ? ` · ${fmtMDY(x.start_date)}` : ""}`])]} />
      <Field label="Registration Opens (optional, ISO or date)" testid="pf-reg-open" value={p.registration_open_at} onChange={(v) => upd({ registration_open_at: v })} />
      <Field label="Registration Closes (optional, ISO or date)" testid="pf-reg-close" value={p.registration_close_at} onChange={(v) => upd({ registration_close_at: v })} />
      <Field label="Sort Order" type="number" testid="pf-sort" value={p.sort_order} onChange={(v) => upd({ sort_order: v })} />
      <LiveAgreementPreview p={p} token={token} />
    </>
  );
}
